import { TabularExportContext } from '@ecosystem/export-api';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import {
  ExportContext,
  ExportFileName,
  FilterKeys,
  Filters,
  Option,
  PageName,
  PageType,
} from '@utils/pageConfig/Types';
import { kpiDisplayNameFromHeader } from '@models/PerformanceAnalysisboard';
import { ErrorTag, ErrorTagEnum, saveErrorLog } from '@utils/LogUtil';
import { ExportFormat } from '@ecosystem/newron-client-lib';
import { endOfDay, format } from 'date-fns';
import { getInvalidPGs } from '@utils/CountryUtils';
import { Periodicity } from '@components//time-period-selector/PeriodsTypes';
import { PerformanceAnalysisView } from '@components//performance-analysis-card/Types';
import { ExportTrackingPageView } from './export-tracking/ExportTracking';
import { DATA_POINT_SIZE_BASED_ON } from '@components//opportunity-review-filter/OpportunityReviewChartFilter';
import { DEFAULT_VOLUME } from '@utils/factVolumeUtils/FactVolumeUtils';
import { ToastType } from '@components//toast-message/ToastMessage';
import { BubbleChartParam, ExportChartType, ExportChartTypeAndParams, TableParam } from '@models/OpportunityReview';
import { isPeriodYTDOrMAT, removeYTDMATPeriodLabel } from '@utils/PeriodUtils';

export const getProductGroupContext = (appliedPg: Option[] | undefined) => {
  if (!appliedPg || appliedPg.length === 0) {
    return '-';
  }

  const pgList = appliedPg.map((pg) => {
    return pg.label;
  });

  return pgList.toString();
};

export const getContext = (exportContext: ExportContext): TabularExportContext => {
  const contextStrings = generateContextStrings(exportContext);
  const contextArray = [
    ['Country', contextStrings.countryText],
    ['Product group', contextStrings.pgText],
    ['Time Frame', contextStrings.periodText],
  ];

  const { pageView, pageName, selectedKpi } = exportContext;
  if (pageView === ExportTrackingPageView.BubbleChart) {
    contextArray.push(['Display by', selectedKpi!], [DATA_POINT_SIZE_BASED_ON, exportContext.segmentBy!]);
  }

  if (pageView === ExportTrackingPageView.BarChart) {
    contextArray.push(['KPI', selectedKpi ?? '-']);
  }

  if (
    pageName !== PageName.CATEGORY_LEADERBOARD &&
    pageName !== PageName.RI_PRODUCT &&
    pageName !== PageName.RI_PRODUCT_COMPARE &&
    pageName !== PageName.RETAILER_OVERVIEW
  ) {
    const showLabel = getContextLabel(pageName);
    const showLabelText = exportContext.selectedKpi
      ? `${contextStrings.showPerformanceWithinText} by ${exportContext.selectedKpi}`
      : contextStrings.showPerformanceWithinText;
    contextArray.push([showLabel, showLabelText]);

    if (exportContext.sortedBy) {
      contextArray.push(['Sorted By', exportContext.sortedBy]);
    }
  }

  contextArray.push(['Filters', contextStrings.filtersText]);
  return contextArray as TabularExportContext;
};

const getContextLabel = (pageName: PageName | undefined): string => {
  switch (pageName) {
    case PageName.OPPORTUNITY_REVIEW:
      return 'Show opportunities within';
    case PageName.CATEGORY_LEADERBOARD:
    case PageName.RI_PRODUCT_COMPARE:
    case PageName.RETAILER_OVERVIEW:
      return '';
    case PageName.RI_PRODUCT_TRENDS:
    case PageName.EXPLORE:
    case PageName.TRADING_ASSISTANT:
      return 'Show';
    default:
      return 'Show performance within';
  }
};

const getFiltersText = (text: string): string => {
  return `\n${text === '-' ? '' : text}`;
};

export const getContextPowerPoint = (exportContext: ExportContext): TabularExportContext => {
  const { countryText, pgText, periodText, showPerformanceWithinText, filtersText } =
    generateContextStrings(exportContext);

  const showLabel = getContextLabel(exportContext.pageName);
  const selectedKpi = exportContext.selectedKpi ?? '';
  const sortedBy = exportContext.sortedBy ?? '';

  const header = `${countryText} - Product group: ${pgText} - Time frame: ${periodText}`;

  if (!showLabel) {
    return [['Country', `${header}\n${getFiltersText(filtersText)}`]];
  }

  const firstPart = selectedKpi
    ? `${showLabel}: ${showPerformanceWithinText} by ${selectedKpi}`
    : `${showLabel}: ${showPerformanceWithinText}`;

  const parts = [firstPart, sortedBy && `Sorted By ${sortedBy}`, filtersText].filter(Boolean);

  return [['Country', `${header}\n${parts.join(' | ')}`]];
};

const generateContextStrings = ({
  appliedCountry,
  appliedPg,
  timePeriod,
  comparisonTimePeriod,
  periodType,
  showPerformanceWithin,
  appliedFiltersText,
  pageName,
  pagesConfig,
  loadedProductGroups,
  loadedCountries,
}: ExportContext) => {
  const period = removeYTDMATPeriodLabel(timePeriod);
  const comparisonPeriod = removeYTDMATPeriodLabel(comparisonTimePeriod);
  let timeFrameText = `${periodType} (${period} vs ${comparisonPeriod})`;
  if (
    (pageName === PageName.RI_PRODUCT || pageName === PageName.RI_PRODUCT_COMPARE) &&
    isPeriodYTDOrMAT({ label: timePeriod, value: timePeriod, lastUpdatedDate: 0 })
  ) {
    timeFrameText = `${periodType} (${period} vs No Comparison)`;
  }

  const countryText = appliedCountry ? appliedCountry.map((countries) => countries.label).toString() : '-';
  const countryWithLoadedStatusText = loadedCountries
    ? `${countryText} (${
        loadedCountries.subscribedCountries.length - loadedCountries.unavailableCountriesByPeriodAndPg.length
      }/${loadedCountries.subscribedCountries.length} countries loaded)`
    : countryText;

  // Retailer Overview & Category Leaderboard & Explore have special handling, refer to getPGContextForROCL function for logic
  const isMultiCountry = appliedCountry && appliedCountry.length > 1;
  const productGroupText =
    pageName === PageName.CATEGORY_LEADERBOARD ||
    pageName === PageName.RETAILER_OVERVIEW ||
    pageName === PageName.EXPLORE ||
    pageName === PageName.OPPORTUNITY_REVIEW
      ? getProductGroupContextForMultiPg(appliedPg, pagesConfig)
      : getProductGroupContext(appliedPg);
  const productGroupWithLoadedStatusText = loadedProductGroups
    ? `${productGroupText} (${loadedProductGroups.totalAvailablePgs}/${loadedProductGroups.totalSubscriptionPgs} Category loaded)`
    : productGroupText;

  const periodText = periodType !== '' && timePeriod !== '' ? timeFrameText : '-';

  const showPerformanceWithinText = showPerformanceWithin !== '' ? showPerformanceWithin : '-';

  const filtersText = appliedFiltersText !== '' ? appliedFiltersText : '-';

  return {
    countryText: countryWithLoadedStatusText,
    pgText: isMultiCountry ? productGroupText : productGroupWithLoadedStatusText,
    periodText,
    showPerformanceWithinText,
    filtersText,
  };
};

//For CL, RO and OR export: it might have no PG selected so PG header will be All, if selected PG don't have data should be excluded
export const getProductGroupContextForMultiPg = (appliedPg: Option[] | undefined, pagesConfig: PagesConfig) => {
  if (!appliedPg) {
    return '-';
  }
  if (appliedPg && appliedPg.length === 0) {
    return 'All';
  }

  const countries = pagesConfig.appliedFilters[pagesConfig.pageType].selectedFilters
    .filter((f) => f.filterKey === FilterKeys.COUNTRY)
    .flatMap((f) => f.options);

  const invalidPGs = getInvalidPGs(
    pagesConfig.selectedTimeFrame as Periodicity,
    appliedPg,
    countries,
    pagesConfig.pgsByCountry,
  );
  const availablePg = appliedPg.filter((pg) => !invalidPGs.includes(pg));
  const pgList = availablePg.map((pg) => {
    return pg.label;
  });
  return pgList.join(', ');
};

export const getProductGroupFile = (
  appliedPg: Option[] | undefined,
  pageName?: PageName,
  isMultiCountry?: boolean | undefined,
) => {
  //As per AC CL & RO export should not have any PG name in file
  if (
    pageName === PageName.CATEGORY_LEADERBOARD ||
    pageName === PageName.RETAILER_OVERVIEW ||
    pageName === PageName.EXPLORE
  ) {
    return '';
  }
  if (!appliedPg || appliedPg.length === 0) {
    return '-pg';
  }
  return `${isMultiCountry ? '' : '-'}${appliedPg[0].label}`;
};

export const getFilename = ({
  title,
  appliedCountry,
  appliedPg,
  timeframe,
  comparisonTimePeriod,
  exportFormat,
  selectedKpi,
  viewType,
  pageName,
}: ExportFileName): string => {
  /**
   * For Excel: only PA Stacked Chart filename should have KPI name(cause only selected KPI is exported)
   * For Png/PPTX: any export should have KPI name because not exporting all the KPI data in these format
   */
  const isExportExcel = exportFormat === ExportFormat.Xlsx;
  const isExportPAStackedChart =
    pageName === PageName.PERFORMANCE_ANALYSIS && viewType === PerformanceAnalysisView.StackedBarChart;

  //For Performance Analysis Stacked chart, need to include KPI label text
  //For excel file, no need to include kpi label text in file name
  //For Product Trends page, need to include kpi label text in file name
  //For Product Compare Model Export, all Excel, PNG and PPTX no need to include KPI label text in file name
  // For Retailer Overview Average Selling Price Line Chart, no need to include KPI label text in file name

  const isToIncludeKpiLabelInFileName =
    (isExportPAStackedChart || !isExportExcel || pageName === PageName.RI_PRODUCT_TRENDS) &&
    pageName !== PageName.RI_PRODUCT_COMPARE &&
    pageName !== PageName.RETAILER_OVERVIEW &&
    pageName !== PageName.TRADING_ASSISTANT;
  const kpiText = isToIncludeKpiLabelInFileName ? `${selectedKpi.label}-` : '';

  const period = removeYTDMATPeriodLabel(timeframe);
  const comparisonPeriod = removeYTDMATPeriodLabel(comparisonTimePeriod);
  let remainingText = `${period} vs ${comparisonPeriod}`;
  if (
    (pageName === PageName.RI_PRODUCT || pageName === PageName.RI_PRODUCT_COMPARE) &&
    isPeriodYTDOrMAT({ label: timeframe, value: timeframe, lastUpdatedDate: 0 })
  ) {
    remainingText = `${period} vs No Comparison`;
  }
  const multiCategoryFileNameSuffix = `${removeYTDMATPeriodLabel(timeframe)} vs ${removeYTDMATPeriodLabel(
    comparisonTimePeriod,
  )}`;

  const isMultiCountry = appliedCountry && appliedCountry.length > 1;

  const countryLabel = isMultiCountry ? '' : appliedCountry?.[0].label;

  if (pageName === PageName.EXPLORE || pageName === PageName.OPPORTUNITY_REVIEW) {
    return `${title}-${multiCategoryFileNameSuffix}`;
  }

  return `${countryLabel ?? 'country'}${getProductGroupFile(
    appliedPg,
    pageName,
    isMultiCountry,
  )}-${title}-${kpiText}${remainingText}`;
};

export const getAppliedFiltersText = (pagesConfig: PagesConfig, pageType: PageType) => {
  const appliedFilters = pagesConfig.appliedFilters[pageType].selectedFilters.filter(
    (filter) => filter.filterKey !== FilterKeys.COUNTRY && filter.filterKey !== FilterKeys.PRODUCT_GROUP,
  );

  //Prepare applied filters text for excel
  let appliedFiltersTextArray: string[] = [];
  let appliedFiltersText: string;
  appliedFilters.forEach((filter) => {
    let optionsTextArray: string[] = [];
    filter.options.forEach((option) => {
      optionsTextArray.push(option.label);
    });
    return appliedFiltersTextArray.push(`${getFilterTitle(filter)}: ${optionsTextArray.join(', ')}`);
  });
  appliedFiltersText = appliedFiltersTextArray.join(' | ');

  return appliedFiltersText;
};

/**
 * Returns the display title for a filter.
 * Adds an exclusion prefix if the filter is marked as exclusion.
 *
 * @param filter - The filter object containing title and exclusion status.
 * @returns The formatted filter title string.
 */
const getFilterTitle = (filter: Filters): string => {
  if (filter.isExclusion === true) {
    return `${filter.filterTitle} (Excl.)`;
  }

  return filter.filterTitle;
};

export const getKpiDisplayName = (kpiType: string, volume: string = DEFAULT_VOLUME): string => {
  return kpiDisplayNameFromHeader(volume)
    .filter((kpi) => kpi.value === kpiType)
    .map((kpiDisplay) => kpiDisplay.label)[0];
};

export const getKpiDisplayNameFromHeaderKey = (kpiType: string, volume: string = DEFAULT_VOLUME): string => {
  const kpiDropdownOption = kpiDisplayNameFromHeader(volume).filter((kpi) => kpi.headerKey === kpiType);
  return kpiDropdownOption.length > 0 ? kpiDropdownOption.map((kpiDisplay) => kpiDisplay.label)[0] : '';
};

export const displayExportErrorToastMessage = (
  error: Error,
  errorMessage: string,
  requestPayload: any,
  showToastMessage: ({ message, type, timeout }: { message: string; type?: ToastType; timeout?: number }) => void,
  saveErrorLogInjection?: (e: any, componentMessage: string, tags: ErrorTag) => void,
) => {
  showToastMessage({
    message: 'Something went wrong, please try again later',
    type: 'error',
  });
  if (saveErrorLogInjection) {
    saveErrorLogInjection(error, errorMessage, {
      category: ErrorTagEnum.DataError,
      requestPayload,
    });
  } else {
    saveErrorLog(error, errorMessage, {
      category: ErrorTagEnum.DataError,
      requestPayload,
    });
  }
};

export const getDateTime = function (): string {
  return format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm:ss'Z'");
};

export const getExportChartTypeAndParam = (
  displayTable: boolean | null | undefined,
  swotTableParam?: TableParam | null,
  exportSwotChartParam?: BubbleChartParam | null,
): ExportChartTypeAndParams => {
  if (displayTable === true) {
    return { type: ExportChartType.Table, param: swotTableParam };
  }
  return { type: ExportChartType.Bubble, param: exportSwotChartParam };
};

/**
 Purpose for this function is to customize KPI label to generate Export file name
 Received pageName and Options (KPI label and value) as Parameter
 Currently only 2 conditions either PA or Product Trends
 For PA, label will be customize to add Trend- in the beginning of KPI Label
 For Product Trends, no change is required for the KPI label
 This function only used in the Line Chart Export flow.
 */
export const formatKPILabel = (pageName: PageName, option: Option): Option => {
  const kpiTitle = option?.label?.split(' ');
  if (pageName === PageName.PERFORMANCE_ANALYSIS) {
    return { label: `Trend-${kpiTitle[1]}`, value: '' };
  }
  return option;
};
